import { GraduationCap, Users, Server, Laptop, Network, Mail, Wrench, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import ContactFormModal from '../components/ContactFormModal';

export default function ServicesPage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const trainingServices = [
    {
      icon: <GraduationCap size={32} />,
      title: 'Full Stack Development',
      description: 'Master frontend and backend technologies including React, Node.js, Python, and databases.',
    },
    {
      icon: <Server size={32} />,
      title: 'Cloud Computing',
      description: 'Learn AWS, Azure, and Google Cloud platforms with hands-on projects and certifications.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Cybersecurity',
      description: 'Comprehensive training in network security, ethical hacking, and security operations.',
    },
    {
      icon: <Network size={32} />,
      title: 'Data Science & AI',
      description: 'Python, machine learning, deep learning, and data analytics with real-world applications.',
    },
  ];

  const placementServices = [
    'Resume Building & Optimization',
    'Mock Interviews & Feedback',
    'Aptitude & Technical Test Preparation',
    'Soft Skills Development',
    'Job Portal Assistance',
    'Direct Company Tie-ups',
    'Career Counseling',
    'Post-Placement Support',
  ];

  const itSolutions = [
    {
      icon: <Laptop size={32} />,
      title: 'Computer & Laptop Sales',
      description: 'Wide range of desktops, laptops, and workstations from leading brands with warranty support.',
    },
    {
      icon: <Wrench size={32} />,
      title: 'Hardware Installation',
      description: 'Professional installation and configuration of all types of computer hardware and peripherals.',
    },
    {
      icon: <Network size={32} />,
      title: 'Networking Solutions',
      description: 'Complete LAN/WAN setup, structured cabling, routers, switches, and network infrastructure.',
    },
    {
      icon: <Mail size={32} />,
      title: 'Email Setup & Configuration',
      description: 'Corporate email setup, domain configuration, security implementation, and ongoing maintenance.',
    },
  ];

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.skandaitsolutions.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.skandaitsolutions.com/services"
      }
    ]
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "IT Training and Solutions",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "SKANDA IT SOLUTIONS",
      "url": "https://www.skandaitsolutions.com"
    },
    "areaServed": {
      "@type": "City",
      "name": "Narasaraopeta"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "IT Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "IT Training Programs",
            "description": "Professional IT courses with placement assistance"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "IT Solutions",
            "description": "Hardware sales, networking, and email setup services"
          }
        }
      ]
    }
  };

  return (
    <div data-testid="services-page" className="pt-20">
      <SEO 
        title="IT Services - Training, Placements & Technology Solutions"
        description="SKANDA IT SOLUTIONS offers comprehensive IT services including professional training programs, 100% job placement assistance, computer hardware sales, networking solutions, and email setup services in Narasaraopeta."
        keywords="IT services Narasaraopeta, computer training services, placement services, hardware sales, networking solutions, email setup, IT support, technology solutions, computer repair, software installation"
        canonicalUrl="https://www.skandaitsolutions.com/services"
      />
      
      {/* Structured Data for better indexing */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 data-testid="services-hero-title" className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Services
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Comprehensive IT solutions tailored to meet your training, placement, and technology needs
          </p>
        </div>
      </section>

      {/* IT Trainings */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="training-section-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            IT Training Programs
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Industry-aligned curriculum with hands-on practical training and expert instructors
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {trainingServices.map((service, index) => (
            <div key={index} data-testid={`training-card-${index}`} className="service-card p-8 rounded-sm text-center">
              <div className="icon-wrapper mx-auto">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {service.title}
              </h3>
              <p className="text-slate-600">
                {service.description}
              </p>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 mt-12">
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Training Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-700">
            <div>✓ Live instructor-led sessions</div>
            <div>✓ Real-world projects & assignments</div>
            <div>✓ Industry-recognized certifications</div>
            <div>✓ Flexible batch timings</div>
            <div>✓ Lifetime access to recorded sessions</div>
            <div>✓ Dedicated doubt-clearing support</div>
          </div>
        </div>
      </section>

      {/* Placements */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 data-testid="placement-section-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Placement & Career Support
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive assistance to help you land your dream job in the IT industry
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?auto=format&fit=crop&q=80"
                alt="Placements"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {placementServices.map((service, index) => (
                  <div key={index} data-testid={`placement-item-${index}`} className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                    <Users size={20} className="text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-blue-600 to-sky-500 text-white p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-2">95% Placement Success</h4>
                <p className="text-blue-50">
                  Our students are placed in leading companies like TCS, Infosys, Wipro, Accenture, Amazon, and more.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT Solutions */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="it-solutions-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            IT Solutions for Enterprises
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Complete technology solutions for your business infrastructure needs
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {itSolutions.map((solution, index) => (
            <div key={index} data-testid={`it-solution-card-${index}`} className="service-card p-8 rounded-sm flex space-x-6">
              <div className="icon-wrapper flex-shrink-0">
                {solution.icon}
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {solution.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {solution.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-slate-900 text-white p-10 rounded-lg mt-12">
          <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            End-to-End IT Solutions
          </h3>
          <p className="text-slate-300 text-lg mb-6">
            From consultation to installation and ongoing support, we provide complete technology solutions tailored to your business needs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-800 p-6 rounded-lg">
              <div className="text-2xl font-bold mb-2">Consultation</div>
              <div className="text-slate-400">Expert assessment of your IT requirements</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg">
              <div className="text-2xl font-bold mb-2">Implementation</div>
              <div className="text-slate-400">Professional installation and setup</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg">
              <div className="text-2xl font-bold mb-2">Support</div>
              <div className="text-slate-400">24/7 maintenance and troubleshooting</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 data-testid="services-cta-title" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-10 text-blue-50 max-w-2xl mx-auto">
            Contact us today to learn more about our services and how we can help you achieve your goals.
          </p>
          <button 
            data-testid="services-cta-button" 
            onClick={() => setIsContactModalOpen(true)}
            className="bg-white text-blue-600 px-10 py-4 rounded-sm font-bold text-lg hover:bg-slate-100 transition-all duration-300 hover:shadow-xl"
          >
            Contact Us Now
          </button>
        </div>
      </section>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}